import { store } from '../redux/store';
import { PhotoAlbum, PhotoAlbumWithItems, Photo, CreatePhotoAlbum } from '../types/photoAlbum';
import httpService from './httpService';
import {
  setAlbums,
  setAlbumDetails,
  addAlbum,
  updateAlbum as updateAlbumRedux,
  deleteAlbum as deleteAlbumRedux,
} from '../redux/slices/photoAlbumSlice';

const photoAlbumService = {
  async createPhotoAlbum(album: CreatePhotoAlbum): Promise<PhotoAlbum> {
    const response = await httpService.post<PhotoAlbum>(
      `/networks/communities/${album.communityId}/photo-albums`,
      album,
    );
    if (album.coverPhoto) {
      await this.updatePhotoAlbumCover(album.id, album.coverPhoto, album.communityId);
    }
    store.dispatch(addAlbum({ communityId: album.communityId, album: response.data }));
    return response.data;
  },

  async getPhotoAlbums(
    communityId: number,
    options?: {
      search?: string;
      sortBy?: 'title' | 'date' | 'photoCount';
      sortOrder?: 'asc' | 'desc';
    },
  ): Promise<PhotoAlbum[]> {
    const params = new URLSearchParams({ communityId: communityId.toString() });

    if (options?.search) {
      params.append('search', options.search);
    }
    if (options?.sortBy) {
      params.append('sortBy', options.sortBy);
    }
    if (options?.sortOrder) {
      params.append('sortOrder', options.sortOrder);
    }

    const { data } = await httpService.get<PhotoAlbum[]>(
      `/networks/communities/${communityId}/photo-albums/?${params.toString()}`,
    );
    store.dispatch(setAlbums({ communityId, albums: data }));
    return data;
  },

  async getPhotoAlbumById(
    communityId: number,
    id: string,
    startIndex: number,
    endIndex: number,
  ): Promise<PhotoAlbumWithItems> {
    const { data } = await httpService.get<PhotoAlbumWithItems>(
      `/networks/communities/${communityId}/photo-albums/${id}?start=${startIndex}&end=${endIndex}`,
    );
    store.dispatch(setAlbumDetails({ albumId: id, album: data }));
    return data;
  },

  async deletePhotoAlbum(communityId: number, id: string): Promise<void> {
    await httpService.delete(`/networks/communities/${communityId}/photo-albums/${id}`);
    store.dispatch(deleteAlbumRedux({ communityId, albumId: id }));
  },

  async updatePhotoAlbum(communityId: number, id: string, updatedAlbum: Partial<PhotoAlbum>): Promise<PhotoAlbum> {
    const { data } = await httpService.put<PhotoAlbum>(
      `/networks/communities/${communityId}/photo-albums/${id}`,
      updatedAlbum,
    );
    store.dispatch(updateAlbumRedux({ communityId, album: data }));
    return data;
  },

  async updatePhotoAlbumCover(id: string, file: File, communityId: number): Promise<PhotoAlbumWithItems> {
    const formData = new FormData();
    formData.append('coverPhoto', file);

    const { data } = await httpService.put<PhotoAlbumWithItems>(
      `/networks/communities/${communityId}/photo-albums/${id}/cover`,
      formData,
    );
    return data;
  },

  async addPhotosToAlbum(communityId: number, albumId: string, photos: File[]): Promise<Photo[]> {
    const formData = new FormData();
    photos.forEach((photo) => formData.append('photos', photo));

    const { data } = await httpService.post<Photo[]>(
      `/networks/communities/${communityId}/photo-albums/${albumId}/photos`,
      formData,
    );
    return data;
  },

  async deletePhoto(communityId: number, albumId: string, photoId: number): Promise<void> {
    await httpService.delete(`/networks/communities/${communityId}/photo-albums/${albumId}/photos/${photoId}`);
  },

  async getPhotoAlbumZip(communityId: number, albumId: string): Promise<Blob> {
    const response = await httpService.get(`/networks/communities/${communityId}/photo-albums/${albumId}/zip`, {
      responseType: 'blob',
    });

    if (response.status !== 200) {
      throw new Error('Failed to download album zip');
    }

    return response.data;
  },

  async getTotalItemCountForCommunity(communityId: number): Promise<number> {
    const { data } = await httpService.get<number>(`/networks/communities/${communityId}/photo-albums/items-count`);
    return data;
  },

  async movePhoto(communityId: number, albumId: string, photoId: number, targetAlbumId: string): Promise<void> {
    await httpService.put(`/networks/communities/${communityId}/photo-albums/${albumId}/photos/${photoId}/move`, {
      targetAlbumId,
    });
  },

  async deletePhotos(communityId: number, albumId: string, itemIds: number[]): Promise<void> {
    await httpService.delete(`/networks/communities/${communityId}/photo-albums/${albumId}/photos`, {
      data: { itemIds },
    });
  },

  async movePhotos(communityId: number, albumId: string, itemIds: number[], targetAlbumId: string): Promise<void> {
    await httpService.put(`/networks/communities/${communityId}/photo-albums/${albumId}/photos/move`, {
      itemIds,
      targetAlbumId,
    });
  },
};

export default photoAlbumService;
